import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import "./Pages.css";
import { FACTIONS, useFaction } from "@/contexts/FactionContext";
import { saveFactionPledge } from "@/lib/shownFaction";
import Navigator from "@/components/Navigator";
import QuestBoard from "@/components/QuestBoard";

export default function Home() {
  const { faction, setFaction, hasPledged } = useFaction();
  // Guest sign-in strip (Oct 2026): signed-out players see a friendly
  // nudge; guests can still play everything without signing in.
  const { user, loading: authLoading, login, authEnabled } = useAuth();
  const showGuestSignin = authEnabled && !authLoading && !user;
  const navigate = useNavigate();
  // Picker for the "2/4-Player" CTA — opens a Two-Player / Four-Player chooser.
  const [pickerOpen, setPickerOpen] = useState(false);
  // Two-tap pledge confirmation:
  //   • 1st tap on a card → pledge that faction (updates global faction state); no nav.
  //   • 2nd tap on the SAME already-pledged card → navigate to /profile.
  //   • Tap on a DIFFERENT card → re-pledge to that faction; no nav.
  // The "awaiting second tap" indicator IS the pledged-faction state itself
  // — when `faction === f.value` the card is in confirm mode. No separate
  // state is needed, so the reset is automatic: tapping a different card
  // calls `setFaction(otherValue)` which makes `faction !== originalValue`
  // again, dropping the original card out of confirm mode.
  //
  // `hasPledged` comes from FactionContext (single source of truth). A
  // pledge made via the Profile dropdown also flips it there, so the
  // "second tap → /profile" affordance stays in sync regardless of which
  // surface set the pledge.
  const handleFactionTap = (value) => {
    if (faction === value && hasPledged) {
      navigate("/profile");
    } else {
      setFaction(value);
      saveFactionPledge(value); // keep the account in step with the shield you tapped
    }
  };
  // Default faction for the CTA glow when nothing is pledged yet → Iron Kingdom (gold).
  const glowFaction = faction || "iron-kingdom";
  const pickAndGo = (mode) => {
    setPickerOpen(false);
    navigate(mode === "two" ? "/two-player" : "/four-player");
  };

  return (
    <div
      className="page-root home-root"
      data-testid="home-screen"
      data-faction-value={glowFaction}
      data-faction-pledged={hasPledged ? "true" : "false"}
    >
      <Navigator />
      <div className="page-content">
        <section
          className="hero-block float-card"
          role="button"
          tabIndex={0}
          aria-label="Play Faction Wars"
          data-testid="home-gem-faction-wars"
          style={{ "--gem-url": `url(${process.env.PUBLIC_URL || ""}/faction-gems/${glowFaction}.webp)` }}
          onClick={() => navigate("/four-player?start=faction_wars")}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/four-player?start=faction_wars"); } }}
        >
          <span className="hero-gem-caption" aria-hidden="true">Tap the gem · Faction Wars</span>
          <div className="hero-plaque float-card-frame" data-testid="home-hero-plaque">
            <h1 className="hero-title">
              4X<br /><span className="hero-title-L">L</span>egendary<br />Chess
            </h1>
          </div>
        </section>

        <section className="factions-section" data-testid="home-factions">
          <h2 className="factions-heading">Choose Your Faction</h2>
          <div className="factions-grid">
            {FACTIONS.map((f) => {
              const selected = faction === f.value;
              return (
                <button
                  type="button"
                  key={f.value}
                  onClick={() => handleFactionTap(f.value)}
                  className={`faction-shield ring-card ${selected ? "is-selected" : ""}`}
                  data-faction-value={f.value}
                  data-testid={`faction-shield-${f.value}`}
                  aria-pressed={selected}
                  style={{ "--shield-glow": f.glow }}
                >
                  {/* Solid faction-tint wash sitting BETWEEN the ring
                      animation (.faction-shield::before, z-index:0) and
                      the shield/text content (z-index:1). Pointer-event
                      transparent so it never blocks clicks/taps. */}
                  <span
                    className="faction-tint"
                    aria-hidden="true"
                    data-testid={`faction-tint-${f.value}`}
                  />
                  <span className="faction-shield-imgwrap">
                    <img
                      src={f.image}
                      alt={`${f.label} shield`}
                      className="faction-shield-img"
                      draggable={false}
                    />
                  </span>
                  <span className="faction-shield-name">{f.label}</span>
                  <span className="faction-shield-tag">{f.tagline}</span>
                  {selected && (
                    <span
                      className="faction-shield-badge"
                      data-testid={`faction-shield-badge-${f.value}`}
                    >
                      Pledged
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        <section className="hero-cta-block float-card">
          <div className="hero-cta-row hero-cta-row-4">
            <Link to="/games" className="cta-btn cta-primary" data-testid="home-cta-games">
              Play a Game
            </Link>
            <Link to="/puzzles" className="cta-btn cta-primary" data-testid="home-cta-puzzles">
              Solve Puzzles
            </Link>
            <button
              type="button"
              className="cta-btn cta-primary"
              onClick={() => setPickerOpen(true)}
              data-testid="home-cta-multiplayer"
            >
              2/4-Player
            </button>
            <Link to="/tournaments" className="cta-btn cta-primary" data-testid="home-cta-tournaments">
              Tournaments
            </Link>
          </div>
        </section>

        {showGuestSignin && (
          <section className="guest-signin float-card" data-testid="home-guest-signin">
            <p className="guest-signin-title">Sign in to save your progress and earn gold</p>
            <button type="button" className="cta-btn cta-primary" onClick={login} data-testid="home-guest-signin-btn">
              Sign In with Google
            </button>
            <p className="guest-signin-fine">
              By signing in you agree to our <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </section>
        )}

        <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
          <DialogContent
            className="home-mp-picker"
            data-testid="home-mp-picker-dialog"
          >
            <DialogHeader>
              <DialogTitle>Local multiplayer</DialogTitle>
              <DialogDescription>
                Pick how many players will share this device.
              </DialogDescription>
            </DialogHeader>
            <div className="home-mp-picker-row">
              <button
                type="button"
                className="cta-btn cta-secondary home-mp-picker-btn"
                onClick={() => pickAndGo("two")}
                data-testid="home-mp-picker-two"
              >
                Two Player
              </button>
              <button
                type="button"
                className="cta-btn cta-secondary home-mp-picker-btn"
                onClick={() => pickAndGo("four")}
                data-testid="home-mp-picker-four"
              >
                Four Player
              </button>
            </div>
          </DialogContent>
        </Dialog>
        <QuestBoard />
      </div>
    </div>
  );
}

